import React, { useEffect, useState } from 'react'

import { Smartphone, Box as BoxIcon } from 'lucide-react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'

import { StatusUpgrade } from '@/core/api/entities/account/statusUpgrade'
import { User } from '@/core/api/entities/user'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useLocations } from '@/core/hooks/useLocation'
import { useValidateNameAvailability } from '@/core/hooks/useZone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { getBuildType } from '@/core/utils/buildType'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'

import { ZoneRequestDTO } from '../Create'

interface ZoneSetupStepProps {
	user: User | null
	accountUpgradeStatus: StatusUpgrade | undefined
	isLoadingStatus: boolean
	form: UseFormReturn<ZoneRequestDTO>
	onZoneTypeSelect: (type: 'single' | 'multiple') => void
	selectedZoneType: 'single' | 'multiple'
	onFormDataChange: (data: Partial<ZoneRequestDTO>) => void
}

const ZoneSetupStep: React.FC<ZoneSetupStepProps> = ({
	user: _user,
	accountUpgradeStatus: _accountUpgradeStatus,
	isLoadingStatus: _isLoadingStatus,
	form,
	onZoneTypeSelect: _onZoneTypeSelect,
	selectedZoneType: _selectedZoneType,
	onFormDataChange,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const {
		control,
		formState: { errors },
	} = form
	const [validatedTimes, setValidatedTimes] = useState(0)

	const hideDeviceTypeSection = getBuildType() === 'musicbrand'

	const storeId = form.watch('store_id')
	const createNewLocation = form.watch('create_new_location')
	const deviceType = form.watch('device_type')

	// For builds that hide the device type section, lock the selection to 'app'
	useEffect(() => {
		if (hideDeviceTypeSection) {
			form.setValue('device_type', 'app')
		}
	}, [hideDeviceTypeSection, form])

	// Use our custom hooks
	const nameValidation = useValidateNameAvailability({
		config: useAccountConfig({}),
		options: {
			onSuccess: (response: boolean) => {
				setValidatedTimes(validatedTimes + 1)
				form.setValue('has_valid_zone_name', response)
				onFormDataChange({
					store_id: storeId,
					create_new_location: createNewLocation,
					has_valid_zone_name: response,
				})
			},
		},
	})

	// Locations
	const {
		data: locations,
		isLoading: _isLoadingLocations,
		refetch: refetchLocations,
	} = useLocations({
		config: useAccountConfig({}),
	})

	// Fetch locations when component mounts
	useEffect(() => {
		refetchLocations()
	}, [form.getValues('account_id'), refetchLocations])

	// Create a debounced function for name validation
	const debouncedValidation = useDebouncedCallback(
		(name: string, accountId: number) => {
			if (name?.length >= 3) {
				nameValidation.mutate({
					name,
					accountId,
				})
			}
		},
		500
	)

	// Handle zone name validation
	const handleNameChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
		onChange: (e: any) => void
	) => {
		onChange(e) // Update form
		// Use the debounced function instead of calling validate directly
		debouncedValidation(e.target.value, form.getValues('account_id') || 0)
	}

	const options =
		locations?.data?.map((location) => ({
			value: location.id.toString(),
			label: location.name,
		})) || []

	// Prepare location options
	const locationOptions = [
		{ value: '-1', label: tCommon('entities.zone.create.location.add_new') },
		...options,
	]

	// Handle location selection
	const handleLocationChange = (value: string) => {
		const numValue = parseInt(value)
		form.setValue('store_id', numValue)
		onFormDataChange({
			store_id: numValue,
			create_new_location: numValue === -1,
			has_valid_zone_name: form.watch('has_valid_zone_name'),
		})
	}

	const handleDeviceTypeSelect = (type: 'app' | 'box') => {
		form.setValue('device_type', type)
		onFormDataChange({
			device_type: type,
		})
	}

	const hasInvalidNameError =
		!form.getValues('has_valid_zone_name') &&
		form.getValues('name')?.length > 0 &&
		validatedTimes > 0

	return (
		<div className="space-y-8 w-full max-w-2xl min-w-0">
			<div>
				<h2 className="text-lg font-semibold mb-2">
					{t('create.zoneSetup.title')}
				</h2>
			</div>

			<div className="space-y-2 min-w-0">
				<Label className="text-sm font-medium block mb-1">
					{t('create.zoneSetup.locationLabel')}
				</Label>
				<Controller
					control={control}
					name="store_id"
					render={({ field }) => (
						<Select
							value={field.value?.toString()}
							onValueChange={handleLocationChange}
						>
							<SelectTrigger className="bg-input border-input w-full max-w-full min-w-0 overflow-hidden">
								<SelectValue
									placeholder={tCommon(
										'entities.zone.create.form.location.placeholder'
									)}
								/>
							</SelectTrigger>
							<SelectContent className="max-h-[min(16rem,70vh)] max-w-[calc(100vw-2rem)]">
								{locationOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										<span className="truncate">{option.label}</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.store_id?.message && (
					<p className="text-sm text-red-500">{errors.store_id.message}</p>
				)}
			</div>

			<div className="space-y-2">
				<Label className="text-sm font-medium block mb-1">
					{t('create.zoneSetup.zoneNameLabel')}
				</Label>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							{...field}
							placeholder={t('create.zoneSetup.zoneNamePlaceholder')}
							onChange={(e) => handleNameChange(e, field.onChange)}
							className={`bg-input border-input ${
								hasInvalidNameError ? 'border-red-500' : ''
							}`}
						/>
					)}
				/>
				{errors.name?.message && (
					<p className="text-sm text-red-500">{errors.name.message}</p>
				)}
				{hasInvalidNameError && (
					<p className="text-sm text-red-500">
						{t('create.zoneSetup.zoneNameNotAvailable')}
					</p>
				)}
			</div>

			{!hideDeviceTypeSection && (
				<div className="space-y-3">
					<Label className="text-sm font-medium block mb-2">
						{t('create.zoneSetup.deviceTypeLabel')}
					</Label>
					<div className="flex flex-col md:flex-row gap-4">
						<button
							type="button"
							onClick={() => handleDeviceTypeSelect('app')}
							className={`relative p-6 rounded-lg transition-all border-2 cursor-pointer ${
								deviceType === 'app'
									? 'bg-shade-light border-primary-foreground/70'
									: 'bg-shade opacity-30'
							}`}
						>
							<div className="flex md:flex-col items-start gap-3">
								<Smartphone className="h-8 w-8" />
								<div className="text-left">
									<h3 className="font-semibold text-lg">
										{t('create.zoneSetup.deviceTypes.app.title')}
									</h3>
									<p className="text-sm text-muted-foreground mt-1">
										{t('create.zoneSetup.deviceTypes.app.description')}
									</p>
								</div>
							</div>
						</button>

						<button
							type="button"
							onClick={() => handleDeviceTypeSelect('box')}
							className={`relative p-6 rounded-lg transition-all border-2 cursor-pointer ${
								deviceType === 'box'
									? 'bg-shade-light border-primary-foreground/70'
									: 'bg-shade opacity-30'
							}`}
						>
							<div className="flex flex-col items-start gap-3">
								<BoxIcon className="h-8 w-8" />
								<div className="text-left">
									<h3 className="font-semibold text-lg">
										{t('create.zoneSetup.deviceTypes.box.title')}
									</h3>
									<p className="text-sm text-muted-foreground mt-1">
										{t('create.zoneSetup.deviceTypes.box.description')}
									</p>
								</div>
							</div>
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

export default ZoneSetupStep
